<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { ButtonMain } from '@caks/uikit/button'
import { getVideoOptions } from '@caks/shared/utils/platformHelpers'
import { isPlatform } from '@ionic/vue'
import { postStoriesWatchCount } from '@caks/shared/api/user.route'

const modules = [Pagination]
const router = useRouter()

const swiperOptions = {}

interface Props {
  activePaginationColor: string
  paginationBg: string
  stories: Array<{
    id: number
    preview_image: string
    media: string
    media_type: 'image' | 'video'
    duration: number
    button: {
      exists: boolean
      link: string
      text: string
      is_internal_link: boolean
    }
  }>
  desktop?: boolean
  startIndex?: number
}

interface Emit {
  (e: 'close'): void
  (e: 'watch-story', id: number): void
}

const props = withDefaults(defineProps<Props>(), {
  desktop: false,
  startIndex: 0,
})

const emit = defineEmits<Emit>()
let watched: number[] = []
const isMount = ref(false)
const stopAnimation = ref(false)
const directionAnimation = ref(0)
const remainingTime = ref(0)
const startTimer = ref(0)

onMounted(() => {
  const watchString = localStorage.getItem('wathed-stories')

  if (watchString) {
    watched = JSON.parse(watchString)
  }

  isMount.value = true
})

const propgress = ref(0)
const propgessBar = computed(
  () =>
    `linear-gradient(to right, ${props.activePaginationColor} ${propgress.value}%, ${props.paginationBg} ${propgress.value}%)`
)

const slides = ref<Array<HTMLElement>>([])
const sliderInstance = ref<any>()
const currentSlideLoaded = ref(true)
const bulletsElements = ref<NodeListOf<HTMLElement>>()
const videos = ref()

let interval: ReturnType<typeof setInterval>
let timeout: ReturnType<typeof setTimeout>
let vidDuration: number | null = null

const checkIfCloseNeeded = () => {
  if (sliderInstance.value.activeIndex === slides.value.length - 1) {
    emit('close')
  }
}

const resetProgress = () => {
  clearInterval(interval)
  clearTimeout(timeout)
  currentSlideLoaded.value = false
  propgress.value = 0
  vidDuration = null
}

const skipAfterDelay = () => {
  clearTimeout(timeout)
  propgress.value = 0
  const baseDuration = props.stories[sliderInstance.value.activeIndex].duration * 1000
  const duration = vidDuration || baseDuration
  const increment = (100 / duration) * 5

  remainingTime.value = duration

  //todo: переделать на канвас
  interval = setInterval(() => {
    if (stopAnimation.value || !currentSlideLoaded.value) return
    propgress.value += increment
  }, 5)

  startTimer.value = Date.now()
  timeout = setTimeout(() => {
    checkIfCloseNeeded()
    clearInterval(interval)
    vidDuration = null
    sliderInstance.value.slideNext()
    autoplay()
  }, duration)
}

const stopVideos = () => {
  for (let i = 0; i < videos.value?.length; i++) {
    const element = videos.value[i]
    element.pause()
    element.currentTime = 0
  }
}

const autoplay = () => {
  resetProgress()
  const vid = slides.value[sliderInstance.value.activeIndex].querySelector('video')

  if (!vid) {
    const image = slides.value[sliderInstance.value.activeIndex].querySelector('img')
    if (image?.complete) {
      currentSlideLoaded.value = true
      skipAfterDelay()
    } else {
      image?.addEventListener('load', () => {
        currentSlideLoaded.value = true
        skipAfterDelay()
      })
    }

    return
  }

  if (vid?.readyState === 4) {
    vidDuration = vid.duration * 1000
    currentSlideLoaded.value = true
    vid?.play()
    skipAfterDelay()
  } else {
    vid?.addEventListener('canplaythrough', () => {
      vidDuration = vid.duration * 1000
      currentSlideLoaded.value = true
      vid?.play()
      skipAfterDelay()
    })
  }
}

const slideNextOnTap = (e: any, index: number) => {
  resetProgress()
  if (e.offsetX > slides.value[index].offsetWidth / 2) {
    checkIfCloseNeeded()
    sliderInstance.value.slideNext()
  } else {
    sliderInstance.value.slidePrev()
  }
  autoplay()
}

const initSwiper = (instance: any) => {
  const storyId = props.stories[instance.activeIndex].id
  emit('watch-story', storyId)
  if (instance.activeIndex === 0) {
    if (!watched.includes(storyId)) {
      postStoriesWatchCount(storyId)
    }
  }
  watched.push(storyId)

  sliderInstance.value = instance

  setTimeout(() => {
    if (typeof document !== 'undefined') {
      bulletsElements.value = document.querySelectorAll('.bullet') as NodeListOf<HTMLElement>
      bulletsElements.value.forEach((element, index) => {
        if (index < instance.activeIndex) {
          element.classList.add('bullet_watched')
        } else {
          element.classList.remove('bullet_watched')
        }
      })
    }
  }, 10)
  autoplay()
}

const videoOptions = getVideoOptions()

const handleTouchMove = (event: TouchEvent) => {
  const touch = event.touches[0]
  const startY = touch.pageY
  let direction = ''

  if (typeof document !== 'undefined') {
    document.addEventListener('touchmove', (event) => {
      const touch = event.touches[0]
      const endY = touch.pageY

      if (endY < startY + 60) {
        direction = 'up'
      } else if (endY > startY + 60) {
        direction = 'down'
      }
    })

    document.addEventListener('touchend', () => {
      if (direction === 'down') {
        emit('close')
      }
    })
  }
}

const handleTouchStart = () => {
  stopAnimation.value = true
  if (timeout) {
    clearTimeout(timeout)
    directionAnimation.value = Date.now() - startTimer.value
  }
}

const handleTouchEnd = () => {
  stopAnimation.value = false
  const direction = remainingTime.value - directionAnimation.value
  remainingTime.value = direction
  startTimer.value = Date.now()
  timeout = setTimeout(() => {
    checkIfCloseNeeded()
    clearInterval(interval)
    vidDuration = null
    sliderInstance.value.slideNext()
    autoplay()
    directionAnimation.value = 0
  }, direction)
}

onUnmounted(() => resetProgress())

const onSlideChange = (swiper: any) => {
  const storyId = props.stories[swiper.activeIndex].id
  emit('watch-story', storyId)
  if (!watched.includes(storyId)) {
    postStoriesWatchCount(storyId)
  }
  watched.push(storyId)

  resetProgress()

  if (bulletsElements.value) {
    bulletsElements.value.forEach((element, index) => {
      if (index < swiper.activeIndex) {
        element.classList.add('bullet_watched')
      } else {
        element.classList.remove('bullet_watched')
      }
    })
  }
  if (sliderInstance.value) {
    autoplay()
  }
}

const clickHandle = (story: {
  id: number
  preview_image: string
  media: string
  media_type: 'image' | 'video'
  duration: number
  button: {
    exists: boolean
    link: string
    text: string
    is_internal_link: boolean
  }
}) => {
  emit('close')

  story.button.link.replace('item', '/food')
  const path = story.button.link

  if (path.includes('https')) {
    window.open(path, '_blank')
  } else {
    router.push(path)
  }
}

const visibleHeight = computed(() => {
  return isPlatform('capacitor') ? '100vh' : '100dvh'
})

const isEnd = ref(false)
const sliderMove = (swiper: any) => {
  const isLastSlide = !swiper.isBeginning && swiper.isEnd

  if (isLastSlide) {
    if (isEnd.value) {
      emit('close')
    } else {
      isEnd.value = true
    }
  } else {
    isEnd.value = false
  }
}
</script>

<template>
  <swiper
    v-if="isMount"
    v-bind="swiperOptions"
    ref="swiper"
    :pagination="{
      type: 'bullets',
      bulletActiveClass: 'active-story',
      bulletClass: 'bullet',
      bulletElement: 'area',
    }"
    @swiper="initSwiper"
    @activeIndexChange="onSlideChange"
    @transition-start="sliderMove"
    :initialSlide="startIndex"
    :modules="modules"
    class="slider"
    :class="{ desktop }"
  >
    <swiper-slide v-for="(story, index) of stories" :lazy="true">
      <div
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
        @touchmove="handleTouchMove"
        class="slide__wrapper"
      >
        <div @click="slideNextOnTap($event, index)" ref="slides" class="slide">
          <div :class="{ slide__content_visible: currentSlideLoaded }" class="slide__content">
            <img v-if="story.media_type === 'image'" :src="story.media" alt="" />
            <video
              v-else-if="story.media_type === 'video'"
              ref="videos"
              v-bind="videoOptions"
              loading="lazy"
            >
              <source :src="story.media" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div :class="{ loading_visible: !currentSlideLoaded }" class="slide__skeleton loading">
            <skeletor class="loading__skeleton" />
            <div class="loading__loader">
              <!-- <ion-loading /> -->
              <!-- <slot name="loader" /> -->
            </div>
          </div>
        </div>

        <div v-if="story.button.exists" class="slide__button">
          <button-main class="slide__button-el" @click="clickHandle(story)">
            {{ story.button.text }}
          </button-main>
        </div>
      </div>
    </swiper-slide>
  </swiper>
</template>

<style scoped lang="scss">
.slider {
  border-radius: 16px 16px 0 0;
}
* {
  &:deep(.swiper-lazy-preloader) {
    display: none;
  }
  &:deep(.bullet) {
    display: block;
    width: 100%;
    height: 4px;
    background: v-bind(paginationBg);
    border-radius: 10px;
  }
  &:deep(.bullet_watched) {
    background: v-bind(activePaginationColor);
  }
  &:deep(.active-story) {
    background: v-bind(propgessBar);
  }
  &:deep(.swiper-pagination) {
    top: 16px;
    display: flex;
    width: 100%;
    justify-content: center;
    gap: 4px;
    height: 4px;
    padding: 0 16px;
  }
}

.slide {
  max-width: 100%;
  width: 100%;
  min-height: 100%;
  height: calc(v-bind(visibleHeight) - 40px - $safe_area_top);
  position: relative;

  &__wrapper {
    position: relative;
    height: calc(0px + 100%);
    min-height: 100%;
  }
  &__button {
    position: absolute;
    z-index: 3;
    left: 0;
    bottom: calc(44px + $safe_area_bottom);
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 0 16px;
  }
  &__button-el {
    width: 100%;
  }
  &__content {
    position: absolute;
    z-index: 2;
    height: 100%;
    width: 100%;
    opacity: 0;

    > * {
      height: calc(100vh - 40px - $safe_area_top);
    }

    &_visible {
      opacity: 1;
    }
  }
  &__skeleton {
    width: 100%;
    position: absolute;
    border-radius: 0;
    height: 100%;
    z-index: 1;
    top: 0;
    left: 0;
    min-height: 100%;
  }
}

.desktop {
  width: 448px;
  border-radius: 16px;

  .slide {
    width: 448px;
    min-width: 448px;
    height: 760px;
    position: relative;

    img,
    video {
      min-width: 448px;
      object-fit: cover;
    }

    &__wrapper {
      position: relative;
      height: calc(0px + 100%);
      min-height: 100%;
    }

    &__button {
    }
    &__button-el {
    }
    &__content {
      > * {
        height: 760px;
      }
      &_visible {
      }
    }
    &__skeleton {
    }
  }
}

.loading {
  display: none;
  position: relative;

  &_visible {
    display: block;
  }

  &__skeleton {
    border-radius: 0;
  }

  &__loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
